import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import { ReviewCard } from "@/components/ReviewCard";
import { toast } from "sonner";
import { GraduationCap, BookOpen, Calculator, Check, ArrowRight, Mail, Clock, Languages, Coffee } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1772396867158-e26d9e6256b2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const PRICING_IMG = "https://images.pexels.com/photos/64779/pexels-photo-64779.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SUBJECTS_IMG = "https://images.unsplash.com/photo-1605116959031-6e2d13a2ee56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const Section = ({ children, className = "", ...rest }) => (
  <section className={`max-w-7xl mx-auto px-6 md:px-12 ${className}`} {...rest}>
    {children}
  </section>
);

export default function Home() {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <Section className="pt-16 md:pt-24 pb-20">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <motion.div
            className="md:col-span-7"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="text-sm tracking-widest uppercase text-terracotta font-semibold">
              {t.hero.kicker}
            </span>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-none mt-5 text-ink">
              {t.hero.title}
            </h1>
            <p className="mt-7 text-lg text-ink/70 max-w-xl leading-relaxed">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/reviews"
                data-testid="hero-reviews-btn"
                className="bg-forest text-white hover:bg-forest-hover px-8 py-4 transition-colors text-sm font-semibold tracking-wider inline-flex items-center gap-2"
              >
                {t.hero.cta_reviews} <ArrowRight size={16} />
              </Link>
              <a
                href="#contact"
                data-testid="hero-contact-btn"
                className="border border-forest text-forest hover:bg-forest hover:text-white px-8 py-4 transition-colors text-sm font-semibold tracking-wider"
              >
                {t.hero.cta_contact}
              </a>
            </div>
          </motion.div>
          <motion.div
            className="md:col-span-5 relative"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <img src={HERO_IMG} alt="Tutoring" className="w-full h-[460px] object-cover" />
            <div className="absolute -bottom-5 -left-5 bg-terracotta text-white px-6 py-4 font-semibold tracking-wide shadow-lg">
              {t.hero.badge}
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ABOUT */}
      <div className="bg-surface border-y border-line">
        <Section className="py-20 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img src={SUBJECTS_IMG} alt="About" className="w-full h-[360px] object-cover" />
          </div>
          <div className="md:col-span-7">
            <span className="text-sm tracking-widest uppercase text-terracotta font-semibold">{t.about.title}</span>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4 text-ink">{t.about.name}</h2>
            <p className="mt-6 text-lg text-ink/70 leading-relaxed whitespace-pre-line">{t.about.body}</p>
          </div>
        </Section>
      </div>

      {/* SUBJECTS */}
      <Section className="py-20">
        <span className="text-sm tracking-widest uppercase text-terracotta font-semibold">{t.subjects.title}</span>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4 text-ink max-w-2xl">{t.subjects.intro}</h2>
        <div className="grid md:grid-cols-12 gap-6 mt-12">
          <div className="md:col-span-4 bg-surface border border-line p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid="subject-primary">
            <BookOpen className="text-forest" size={32} />
            <h3 className="font-serif text-2xl mt-5 text-ink">{t.subjects.primary}</h3>
            <p className="mt-3 text-ink/70">{t.subjects.primary_desc}</p>
          </div>
          <div className="md:col-span-4 bg-surface border border-line p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid="subject-secondary">
            <Calculator className="text-forest" size={32} />
            <h3 className="font-serif text-2xl mt-5 text-ink">{t.subjects.secondary}</h3>
            <p className="mt-3 text-ink/70">{t.subjects.secondary_desc}</p>
          </div>
          <div className="md:col-span-4 bg-surface border border-line p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid="subject-dutch">
            <Languages className="text-terracotta" size={32} />
            <h3 className="font-serif text-2xl mt-5 text-ink">{t.subjects.dutch}</h3>
            <p className="mt-3 text-ink/70">{t.subjects.dutch_desc}</p>
          </div>
          <div className="md:col-span-12 bg-surface-alt border border-line p-6 flex items-center gap-3" data-testid="subject-note">
            <GraduationCap className="text-terracotta shrink-0" size={22} />
            <span className="text-ink/80 font-medium">{t.subjects.note}</span>
          </div>
        </div>
      </Section>

      {/* PRICING */}
      <div className="bg-forest text-white">
        <Section className="py-20 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <span className="text-sm tracking-widest uppercase text-terracotta font-semibold">{t.pricing.title}</span>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4 text-white">{t.pricing.kicker}</h2>
            <div className="mt-10 flex items-end gap-4">
              <div>
                <div className="font-serif text-6xl text-white leading-none">€35</div>
                <div className="text-white/60 mt-2 text-sm uppercase tracking-wide">{t.pricing.excl} · {t.pricing.per_hour}</div>
              </div>
              <div className="pb-1">
                <div className="font-serif text-3xl text-terracotta leading-none">€43,75</div>
                <div className="text-white/60 mt-1 text-sm uppercase tracking-wide">{t.pricing.incl}</div>
              </div>
            </div>
            <p className="mt-6 flex items-center gap-2 text-white/80"><Check size={18} className="text-terracotta" /> {t.pricing.business}</p>
            <div className="mt-6 bg-white/10 border border-white/15 p-5 flex items-start gap-3" data-testid="free-consult">
              <Coffee size={22} className="text-terracotta shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-white flex items-center gap-2">
                  {t.pricing.consult_title}
                  <span className="bg-terracotta text-white text-xs px-2 py-0.5 tracking-wide">30 min</span>
                </div>
                <p className="mt-1 text-white/70 text-sm">{t.pricing.consult}</p>
              </div>
            </div>
            <div className="mt-8 border-l-2 border-terracotta pl-5" data-testid="cancellation-policy">
              <div className="flex items-center gap-2 text-sm font-semibold tracking-widest uppercase text-terracotta">
                <Clock size={16} /> {t.pricing.policy_title}
              </div>
              <p className="mt-2 text-white/70 text-sm leading-relaxed max-w-md">{t.pricing.policy}</p>
            </div>
            <a href="#contact" data-testid="pricing-cta" className="mt-10 inline-block bg-terracotta text-white hover:bg-terracotta-hover px-8 py-4 transition-colors text-sm font-semibold tracking-wider">
              {t.pricing.cta}
            </a>
          </div>
          <div className="md:col-span-6">
            <img src={PRICING_IMG} alt="Study planning" className="w-full h-[360px] object-cover" />
          </div>
        </Section>
      </div>

      {/* REVIEWS SNIPPET */}
      <Section className="py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{t.reviewsSnippet.title}</h2>
          <Link to="/reviews" data-testid="all-reviews-link" className="text-forest font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all">
            {t.reviewsSnippet.all} <ArrowRight size={16} />
          </Link>
        </div>
        {reviews.length === 0 ? (
          <p className="mt-10 text-ink/60">{t.reviewsSnippet.empty}</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {reviews.map((r) => (
              <ReviewCard key={r.id} r={r} data-testid="home-review-card" />
            ))}
          </div>
        )}
      </Section>

      {/* CONTACT */}
      <div id="contact" className="bg-surface border-t border-line">
        <Section className="py-20 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="text-sm tracking-widest uppercase text-terracotta font-semibold">{t.contact.title}</span>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4 text-ink">{t.contact.subtitle}</h2>
            <p className="mt-8 text-ink/60 text-sm">{t.contact.or_email}</p>
            <a href="mailto:brickademy66@gmail.com" className="mt-2 inline-flex items-center gap-3 text-forest font-semibold" data-testid="email-link">
              <Mail size={20} /> brickademy66@gmail.com
            </a>
          </div>
          <form onSubmit={submitContact} className="md:col-span-7 space-y-7" data-testid="contact-form">
            <div>
              <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.contact.name}</label>
              <input
                required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-name"
                className="w-full bg-bone border-b-2 border-line rounded-none focus:border-forest px-3 py-3 text-lg transition-colors outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.contact.email}</label>
              <input
                required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="contact-email"
                className="w-full bg-bone border-b-2 border-line rounded-none focus:border-forest px-3 py-3 text-lg transition-colors outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.contact.message}</label>
              <textarea
                required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
                className="w-full bg-bone border-b-2 border-line rounded-none focus:border-forest px-3 py-3 text-lg transition-colors outline-none resize-none"
              />
            </div>
            <button
              type="submit" disabled={sending} data-testid="contact-submit"
              className="bg-forest text-white hover:bg-forest-hover px-8 py-4 transition-colors text-sm font-semibold tracking-wider disabled:opacity-60"
            >
              {sending ? t.contact.sending : t.contact.send}
            </button>
          </form>
        </Section>
      </div>
    </div>
  );
}
